import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CustomerPage, CustomerView, CustomerViewSettings } from '@ste/shared';
import { apiFetch, ApiRequestError } from './auth.ts';

// What owners and members see and may operate (ADR-0020).

const OPEN: CustomerView = {
  hiddenPages: [],
  fixedModes: {},
  lockTargets: false,
  lockHeatPump: false,
  lockBattery: false,
  lockPrices: false,
  restricted: false,
};

export const useCustomerView = (enabled = true) =>
  useQuery({
    queryKey: ['customer-view'],
    // an older gateway without customer view: nothing hidden or locked
    queryFn: () =>
      apiFetch<CustomerView>('/customer-view').catch((error: unknown) => {
        if (error instanceof ApiRequestError && error.status === 404) return OPEN;
        throw error;
      }),
    enabled,
    staleTime: 60_000,
  });

/** locks for the viewer; installers and admins are never restricted */
export function useLocks() {
  const view = useCustomerView().data;
  const active = view?.restricted === true;
  return {
    hidden: (page: CustomerPage) => active && view.hiddenPages.includes(page),
    fixedMode: (loadpointId: number) => (active ? view.fixedModes[String(loadpointId)] : undefined),
    targets: active && view.lockTargets,
    heatpump: active && view.lockHeatPump,
    battery: active && view.lockBattery,
  };
}

export const useInstallerCustomerView = () =>
  useQuery({
    queryKey: ['installer', 'customer-view'],
    queryFn: () => apiFetch<CustomerViewSettings>('/installer/customer-view'),
  });

export function useSaveCustomerView() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (settings: CustomerViewSettings) =>
      apiFetch<CustomerViewSettings>('/installer/customer-view', {
        method: 'PUT',
        body: JSON.stringify(settings),
      }),
    onSuccess: (data) => client.setQueryData(['installer', 'customer-view'], data),
    onSettled: () => {
      void client.invalidateQueries({ queryKey: ['customer-view'] });
      void client.invalidateQueries({ queryKey: ['installer', 'config'] });
      void client.invalidateQueries({ queryKey: ['tariffs'] });
    },
  });
}
